/**
 * Network store — tracks online/offline connectivity.
 *
 * Backed by the Capacitor Network plugin on native (which reports reachability
 * changes reliably inside WKWebView) and by the browser's `navigator.onLine`
 * + online/offline events on the web. `initNetworkMonitor` wires the listeners
 * and should be called once at startup (see lib/native/bootstrap.ts).
 *
 * The offline banner (components/layout/OfflineBanner.tsx) reads `isOnline`,
 * and data-writing code can check `useNetworkStore.getState().isOnline` before
 * attempting a round-trip.
 */

import { create } from 'zustand';
import { Network } from '@capacitor/network';

interface NetworkState {
  /** True when the device has a network connection. Optimistically true. */
  isOnline: boolean;
  /** Connection type reported by the platform: 'wifi' | 'cellular' | 'none' | 'unknown'. */
  connectionType: string;
  setStatus: (isOnline: boolean, connectionType?: string) => void;
}

export const useNetworkStore = create<NetworkState>((set) => ({
  isOnline: true,
  connectionType: 'unknown',
  setStatus: (isOnline, connectionType) =>
    set((s) => ({ isOnline, connectionType: connectionType ?? s.connectionType })),
}));

let started = false;

/** Idempotently subscribe to connectivity changes. Safe to call once at boot. */
export async function initNetworkMonitor(): Promise<void> {
  if (started) return;
  started = true;

  const set = useNetworkStore.getState().setStatus;

  try {
    // Capacitor Network works on web too, but guard so a missing plugin on an
    // old browser can't break startup.
    const status = await Network.getStatus();
    set(status.connected, status.connectionType);
    await Network.addListener('networkStatusChange', (s) => {
      set(s.connected, s.connectionType);
    });
  } catch {
    // Fall back to the raw browser signals.
    if (typeof navigator !== 'undefined') {
      set(navigator.onLine);
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => set(true));
      window.addEventListener('offline', () => set(false));
    }
  }
}

/** Convenience hook: just the boolean. */
export const useIsOnline = () => useNetworkStore((s) => s.isOnline);
